import { Head } from '@inertiajs/react';
import { Heart, Target, Users, Award, BookOpen, Stethoscope } from 'lucide-react';

export default function About() {
  return (
    <>
      <Head title="À propos - Ethos Santé" />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-b from-[#31953a]/10 to-white pt-12 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                À propos d'Ethos Santé
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Une plateforme innovante dédiée à l'amélioration de la relation entre patients et kinésithérapeutes
              </p>
            </div>
          </div>
        </div>

        {/* Notre Mission */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-white rounded-xl shadow-md p-8 md:p-12 mb-12">
            <div className="flex items-center justify-center mb-6">
              <div className="p-4 bg-[#008788]/10 rounded-full">
                <Target className="h-12 w-12 text-[#008788]" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              Notre Mission
            </h2>
            <div className="prose prose-lg max-w-none text-gray-600 space-y-4">
              <p className="text-justify">
                Ethos Santé a pour mission de <strong>faciliter la mise en relation entre patients et kinésithérapeutes</strong> 
                afin d'optimiser la qualité des soins en kinésithérapie. Nous croyons que chaque patient mérite de trouver 
                le professionnel qui correspond parfaitement à ses besoins spécifiques.
              </p>
              <p className="text-justify">
                Dans un contexte où l'offre de soins est parfois difficile à déchiffrer, nous voulons apporter de la 
                <strong> transparence et de la clarté</strong>. Notre plateforme permet aux patients de comprendre les 
                différentes approches thérapeutiques, les techniques utilisées, et les spécialités de chaque praticien.
              </p>
              <p className="text-justify">
                Pour les kinésithérapeutes, Ethos Santé représente une opportunité unique de <strong>valoriser leurs 
                compétences spécifiques</strong>, leurs formations complémentaires et leur expertise. C'est un outil 
                qui leur permet de se différencier et d'attirer une patientèle en adéquation avec leur pratique.
              </p>
            </div>
          </div>

          {/* Nos Valeurs */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Nos Valeurs Fondamentales
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {/* Valeur 1 */}
              <div className="bg-white rounded-xl shadow-md p-8 text-center hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-center mb-4">
                  <div className="p-4 bg-red-100 rounded-full">
                    <Heart className="h-8 w-8 text-red-600" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Caractère & Empathie
                </h3>
                <p className="text-gray-600">
                  Un bon professionnel de santé est celui qui est à l'écoute de vos besoins et de vos attentes. 
                  L'empathie et le caractère sont au cœur d'une relation thérapeutique réussie.
                </p>
              </div>

              {/* Valeur 2 */}
              <div className="bg-white rounded-xl shadow-md p-8 text-center hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-center mb-4">
                  <div className="p-4 bg-blue-100 rounded-full">
                    <Award className="h-8 w-8 text-blue-600" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Valeurs & Éthique
                </h3>
                <p className="text-gray-600">
                  Ethos Santé promeut des soins de qualité et facilite l'accès aux informations pour les patients, 
                  limitant ainsi l'errance thérapeutique et favorisant une prise en charge adaptée.
                </p>
              </div>

              {/* Valeur 3 */}
              <div className="bg-white rounded-xl shadow-md p-8 text-center hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-center mb-4">
                  <div className="p-4 bg-green-100 rounded-full">
                    <BookOpen className="h-8 w-8 text-green-600" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Compétences & Expertise
                </h3>
                <p className="text-gray-600">
                  Nous mettons en valeur les compétences et l'expertise des kinésithérapeutes : des professionnels 
                  formés qui continuent à se former régulièrement avec des formations de qualité.
                </p>
              </div>
            </div>
          </div>

          {/* Notre Approche */}
          <div className="bg-gradient-to-r from-[#008788]/5 to-[#31953a]/5 rounded-xl p-8 md:p-12 mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Notre Approche
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Pour les Patients */}
              <div className="bg-white rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-[#008788]/10 rounded-full mr-3">
                    <Users className="h-6 w-6 text-[#008788]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Pour les Patients
                  </h3>
                </div>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-[#008788] mr-2">•</span>
                    <span>Recherche personnalisée selon vos critères et besoins spécifiques</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#008788] mr-2">•</span>
                    <span>Accès à un blog d'information sur les techniques et approches thérapeutiques</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#008788] mr-2">•</span>
                    <span>Contact facilité avec les professionnels via leur moyen préféré</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#008788] mr-2">•</span>
                    <span>Identification des kinés approuvés par Ethos ayant validé une évaluation de compétences</span>
                  </li>
                </ul>
              </div>

              {/* Pour les Kinésithérapeutes */}
              <div className="bg-white rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-[#31953a]/10 rounded-full mr-3">
                    <Stethoscope className="h-6 w-6 text-[#31953a]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Pour les Kinésithérapeutes
                  </h3>
                </div>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-[#31953a] mr-2">•</span>
                    <span>Mise en valeur de vos formations, matériel et compétences spécifiques</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#31953a] mr-2">•</span>
                    <span>Possibilité de certification Ethos pour valoriser votre expertise</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#31953a] mr-2">•</span>
                    <span>Gain de temps grâce aux filtres de recherche (type de patientèle, disponibilités...)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#31953a] mr-2">•</span>
                    <span>Visibilité accrue auprès d'une patientèle ciblée et adaptée à votre pratique</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Vision */}
          <div className="bg-white rounded-xl shadow-md p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              Notre Vision
            </h2>
            <div className="prose prose-lg max-w-none text-gray-600 space-y-4">
              <p className="text-justify">
                Nous envisageons un avenir où <strong>chaque patient trouve rapidement le kinésithérapeute qui lui 
                correspond</strong>, sans errance thérapeutique ni perte de temps. Un avenir où les professionnels 
                de la kinésithérapie sont reconnus pour leurs compétences spécifiques et leur expertise.
              </p>
              <p className="text-justify">
                Ethos Santé souhaite devenir <strong>la référence en matière de mise en relation patient-kinésithérapeute</strong>, 
                en proposant un service transparent, éthique et centré sur la qualité des soins. Nous voulons contribuer 
                à l'amélioration du système de santé en facilitant l'accès à des soins de kinésithérapie adaptés et de qualité.
              </p>
              <p className="text-justify">
                Notre ambition est de créer une <strong>communauté engagée</strong> où patients et professionnels 
                partagent les mêmes valeurs : l'excellence dans les soins, la transparence et le respect mutuel.
              </p>
            </div>
          </div>

          {/* Call to Action */}
          <div className="mt-16 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Rejoignez-nous dans cette aventure
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Que vous soyez patient à la recherche du bon professionnel ou kinésithérapeute souhaitant valoriser 
              votre expertise, Ethos Santé est fait pour vous.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/search" 
                className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#008788] hover:bg-[#00bcbe] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#008788]"
              >
                Trouver un kiné
              </a>
              <a 
                href="/registration/new" 
                className="inline-flex items-center justify-center px-8 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#008788]"
              >
                Inscription professionnelle
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}



